import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import type { AnswerValue, Assessment, Child, ClientEntry, ClientFlow, IntakeBody } from '@/api/types';

/** Fixed answer ids for the two card kinds that don't carry an `id` on their `ClientEntry`. */
export const STRENGTHS_ID = 'strengths';
export const NOTE_ID = 'note';

const SAVE_DEBOUNCE_MS = 600;

export interface FlowState {
  loading: boolean;
  /** Either a load failure or the current card's validation message — both are i18n keys. */
  error?: string;
  child?: Child;
  assessment?: Assessment;
  flow?: ClientFlow;
  /** Position in `flow.queue`. Equal to `flow.queue.length` once the parent has advanced past the last card. */
  index: number;
  /** Local answers, including ones not yet flushed to the server. */
  answers: Record<string, AnswerValue>;
  save: 'idle' | 'saving' | 'saved' | 'failed';
  savedAt?: number;
}

export interface UseAssessmentFlowResult {
  state: FlowState;
  current: ClientEntry | undefined;
  /** Local set + a 600ms-debounced save of only the ids that changed since the last flush. */
  answer: (id: string, value: AnswerValue) => void;
  /** Validates the current card; on failure sets `state.error` and returns false without advancing. */
  next: () => boolean;
  back: () => void;
  goTo: (index: number) => void;
  /**
   * Force-saves any pending answers now (Next, Save & exit, review, unmount) and resolves with the
   * latest flow the server has returned — after this save, or an earlier one still in flight.
   */
  flush: () => Promise<ClientFlow | undefined>;
  /** sends the pre-questionnaire intake and merges the returned assessment + flow into state.
   * Throws `ApiError` on failure — the caller (the intake UI) owns showing that. */
  submitIntake: (body: IntakeBody) => Promise<ClientFlow>;
  minutesLeft: number;
}

/** Stable identity for a queue entry, independent of its position — used to relocate the
 * current card after the server returns a grown/reordered queue. */
const entryKey = (entry: ClientEntry): string => (entry.kind === 'item' ? `item:${entry.id}` : entry.kind);

const isAnswered = (entry: ClientEntry, answers: Record<string, AnswerValue>): boolean => {
  switch (entry.kind) {
    case 'item': {
      const value = answers[entry.id];
      return value !== undefined && value !== null;
    }
    case 'strengths': {
      const value = answers[STRENGTHS_ID];
      return Array.isArray(value) && value.length > 0;
    }
    case 'note':
      // Optional — never blocks resume/advance on its own.
      return true;
    case 'checkpoint':
      // Informational; no persisted "seen" state, so it's always a valid stopping point.
      return false;
    default:
      return false;
  }
};

const resumeIndex = (queue: ClientEntry[], answers: Record<string, AnswerValue>): number => {
  const idx = queue.findIndex((entry) => !isAnswered(entry, answers));
  return idx === -1 ? Math.max(0, queue.length - 1) : idx;
};

const clampIndex = (index: number, queueLength: number): number =>
  Math.min(Math.max(index, 0), Math.max(queueLength - 1, 0));

/**
 * Merges a freshly-fetched `ClientFlow` into state, keeping the parent on the same card by identity
 * when possible (the queue can grow — or reorder — as they answer) and falling back to the nearest
 * unanswered card otherwise.
 *
 * It never moves the parent to questions the engine added behind them (an easier age band): the
 * page on screen stays as it is, and the assessment page shows those questions next, as a
 * follow-up page, once this one is done (see pagePlan.ts).
 */
const applyFlow = (prev: FlowState, flow: ClientFlow): FlowState => {
  const currentEntry = prev.flow?.queue[prev.index];
  let index: number;
  if (currentEntry) {
    const key = entryKey(currentEntry);
    const found = flow.queue.findIndex((entry) => entryKey(entry) === key);
    index = found !== -1 ? found : resumeIndex(flow.queue, prev.answers);
  } else {
    index = resumeIndex(flow.queue, prev.answers);
  }
  return { ...prev, flow, index: clampIndex(index, flow.queue.length) };
};

export const useAssessmentFlow = (assessmentId: string): UseAssessmentFlowResult => {
  const [state, setState] = useState<FlowState>({ loading: true, index: 0, answers: {}, save: 'idle' });
  const pending = useRef<Record<string, AnswerValue>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The newest flow from the server, and the save still on its way (if any). */
  const lastFlow = useRef<ClientFlow | undefined>(undefined);
  const inflight = useRef<Promise<void> | null>(null);
  // Always-fresh mirror of `state` for `next()` below, so a caller that runs it from a timer or
  // right after `answer()` sees the latest answers without `next` changing identity every keystroke.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    let cancelled = false;
    pending.current = {};
    lastFlow.current = undefined;
    if (timer.current) clearTimeout(timer.current);
    setState({ loading: true, index: 0, answers: {}, save: 'idle' });

    void api.assessments
      .get(assessmentId)
      .then((res) => {
        if (cancelled) return;
        const answers = { ...res.flow.answered } as Record<string, AnswerValue>;
        lastFlow.current = res.flow;
        setState((prev) =>
          applyFlow(
            { ...prev, loading: false, child: res.child, assessment: res.assessment, answers, save: 'idle' },
            res.flow,
          ),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, loading: false, error: 'common.error' }));
      });

    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  const flush = useCallback(async (): Promise<ClientFlow | undefined> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    // A debounced save already on its way decides the queue too: wait for it first.
    if (inflight.current) await inflight.current;
    const batch = pending.current;
    const ids = Object.keys(batch);
    if (ids.length === 0) return lastFlow.current;
    pending.current = {};
    setState((prev) => ({ ...prev, save: 'saving' }));
    const run = (async () => {
      try {
        const payload = Object.fromEntries(ids.map((id) => [id, { value: batch[id] }]));
        const { flow } = await api.assessments.saveAnswers(assessmentId, payload);
        lastFlow.current = flow;
        setState((prev) => applyFlow({ ...prev, save: 'saved', savedAt: Date.now() }, flow));
      } catch {
        // Put the batch back (merged under anything newer) so the next change — or an explicit
        // retry from `SaveIndicator` — tries again rather than losing it.
        pending.current = { ...batch, ...pending.current };
        setState((prev) => ({ ...prev, save: 'failed' }));
      }
    })();
    inflight.current = run;
    await run;
    if (inflight.current === run) inflight.current = null;
    return lastFlow.current;
  }, [assessmentId]);

  const submitIntake = useCallback(
    async (body: IntakeBody): Promise<ClientFlow> => {
      const { assessment, flow } = await api.assessments.setIntake(assessmentId, body);
      lastFlow.current = flow;
      // The intake decides which areas are asked, so the queue before it (only the closing
      // cards) says nothing about where to stand: restart on the first unanswered card rather
      // than following the checkpoint past every new question.
      setState((prev) => ({
        ...prev,
        assessment,
        flow,
        index: clampIndex(resumeIndex(flow.queue, prev.answers), flow.queue.length),
      }));
      return flow;
    },
    [assessmentId],
  );

  const answer = useCallback(
    (id: string, value: AnswerValue) => {
      setState((prev) => ({ ...prev, answers: { ...prev.answers, [id]: value }, error: undefined }));
      pending.current[id] = value;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        void flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  const next = useCallback((): boolean => {
    const snapshot = stateRef.current;
    const entry = snapshot.flow?.queue[snapshot.index];
    if (!entry) return true;

    let errorKey: string | undefined;
    if (entry.kind === 'item' && !isAnswered(entry, snapshot.answers)) errorKey = 'q.needAnswer';
    else if (entry.kind === 'strengths' && !isAnswered(entry, snapshot.answers)) errorKey = 'q.needStrength';

    if (errorKey) {
      setState((prev) => ({ ...prev, error: errorKey }));
      return false;
    }
    setState((prev) => ({ ...prev, error: undefined, index: prev.index + 1 }));
    return true;
  }, []);

  const back = useCallback(() => {
    setState((prev) => ({ ...prev, error: undefined, index: Math.max(prev.index - 1, 0) }));
  }, []);

  const goTo = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      error: undefined,
      index: clampIndex(index, prev.flow?.queue.length ?? 1),
    }));
  }, []);

  // Flush any pending debounced save when this screen goes away — Save & exit and the review
  // hand-off already call `flush()` explicitly, but this is the backstop for every other exit.
  useEffect(
    () => () => {
      void flush();
    },
    [flush],
  );

  const current = state.flow?.queue[state.index];
  const minutesLeft = state.flow
    ? Math.max(1, Math.round(((state.flow.progress.total - state.flow.progress.answered) * 6) / 60))
    : 0;

  return { state, current, answer, next, back, goTo, flush, submitIntake, minutesLeft };
};
